import type { BillingTermKind, CatalogSnapshot } from '~/store/billing'
import type { AppInputAnswers } from '~/utils/app-inputs'
import type { CheckoutConfigurator } from '~/composables/useCheckoutConfigurator'

/**
 * The configuration a visitor built, kept across anything that reloads the page.
 *
 * This started life as a hand-over for one flow — the public page took the
 * customer's details and redirected to `/billing/checkout` to finish — and kept
 * that shape long after the seam went away: it saved only while ANONYMOUS, and
 * erased itself the moment a session appeared. Measured, that meant a customer
 * who signed in got their configuration back exactly once and then had nothing
 * written down at all; a signed-in customer who reloaded came back to a
 * checkout too empty to even price.
 *
 * A page reload is not one event at the start of a purchase. It happens when a
 * social provider replaces the tab, when two-factor hands off to `/login`, when
 * somebody opens their image library or tops up their credit and comes back,
 * and when they simply refresh. So the draft is kept for the whole purchase,
 * signed in or not, and is dropped only when the order is placed.
 *
 * Deliberate choices:
 *
 * - **sessionStorage, not localStorage — the tab IS the expiry.** Every flow
 *   this exists for keeps the tab: a desktop social sign-in runs in a popup so
 *   the opener survives, a mobile one is a same-tab redirect, two-factor and
 *   account selection navigate to `/login` and back, and a WHMCS SSO
 *   click-through is an ordinary page load. So tab-scoping costs us nothing on
 *   any path a customer actually takes to sign in, and it means a half-built
 *   order cannot outlive the sitting that produced it — which is what keeps a
 *   shared machine from offering the next person somebody else's cart.
 *
 *   What it deliberately does NOT do is recover an ABANDONED cart: close the
 *   tab, restart the browser, or open a link in a new one, and the
 *   configuration is gone. That is the trade being made, not an oversight.
 *   Recovering those is a `localStorage` (or better, server-side) decision with
 *   a shared-machine cost attached, and it should be taken as its own change —
 *   including clearing on sign-out, which nothing does today.
 * - **Nothing that grants access to the MACHINE.** The VM's access password is
 *   never written down and is re-entered on return — which the page says out
 *   loud rather than leaving an emptied field to be noticed. Everything else
 *   here is a choice the customer made, and it never leaves their tab.
 * - **Validated against what the account and the catalog actually hold before
 *   it is applied.** A draft is just a set of identifiers; the catalog moves and
 *   the account is not necessarily the one that left. Restoring a retired plan,
 *   or an image belonging to somebody else, would put the customer in a
 *   configuration that cannot be ordered and the refusal would arrive at the
 *   last click. Unknown identifiers are dropped, not restored.
 * - **Account-scoped choices are restored in a SECOND pass**, because the data
 *   that can confirm them (VPCs, images, subscriptions) arrives after the
 *   catalog does — and on a sign-in, after the page has already mounted.
 *   Applying them in the first pass is what made a restore land half-done: the
 *   customer got "private networking" back with no network in it.
 * - **Versioned.** A shape change must invalidate old drafts rather than half
 *   apply to them.
 */

const STORAGE_KEY = 'mw_checkout_draft'
const VERSION = 2
/**
 * IDLE timeout, not an absolute one: `snapshot()` re-stamps `savedAt` on every
 * edit, so this measures six hours since the customer last touched the
 * configuration — not six hours since they started.
 *
 * It is the SECOND expiry, and almost always the looser of the two. The store
 * is tab-scoped, so in practice the tab closes first and this never fires; the
 * cases it does cover are a tab left open and idle overnight, and a browser
 * session-restore, which is the one route by which a draft can outlive the
 * sitting that made it. Do not read this number as the retention policy — the
 * tab is.
 */
const TTL_MS = 6 * 60 * 60 * 1000

export type CheckoutDraft = {
  v: number
  savedAt: number
  datacenterId: number | null
  offeringCode: string | null
  term: BillingTermKind
  osTab: 'os' | 'marketplace' | 'byo'
  templateCode: string | null
  mainDiskExtraGb: number
  extraDisks: { size_gb: number }[]
  addonQuantities: Record<string, number>
  /**
   * Networking. Not a secret and not a catalog code, but it IS a decision the
   * customer made — it was being dropped, so somebody who chose private
   * networking came back on public without being told.
   */
  connectivityMode: 'public' | 'private'
  vpcId: number | null
  /**
   * The three account-scoped choices, all of them previously unsaved.
   *
   * An image id was left out on the grounds that "the account it belongs to may
   * not be the one that comes back" — true, and the answer to it is to check,
   * not to drop it. Dropping it left a customer who chose their own image with
   * no operating system selected at all, which is the loudest possible way to
   * lose a field.
   */
  clientImageId: string | null;
  trialCode?: string | null;
  couponCode: string | null;
  targetSubscriptionId: string | null;
  /** Optional for drafts saved before initial on-demand recharge was offered. */
  initialCreditAmount?: string | null
  /**
   * The chosen app's setup answers, for the app they were given to. NEVER a
   * secret (see `draftableAppInputAnswers`): the page says which to type again.
   * Optional for drafts saved before apps asked anything.
   */
  appInputs?: { templateCode: string; answers: AppInputAnswers } | null
}

/** The parts of the page that are not the configurator. */
export type CheckoutDraftExtras = {
  clientImageId: string | null;
  trialCode?: string | null;
  couponCode: string | null;
  targetSubscriptionId: string | null;
  initialCreditAmount?: string | null;
  appInputs?: { templateCode: string; answers: AppInputAnswers } | null;
};

/** What the account is known to hold, for the second pass. */
export type CheckoutDraftAccountScope = {
  vpcIds?: number[]
  imageIds?: string[]
  subscriptionIds?: string[]
}

export function useCheckoutDraft(trialCode?: string) {
  // A trial is a separate purchase intent. Visiting an offer must not replace
  // the customer's ordinary cart or turn their next paid order into a trial.
  const storageKey = trialCode ? `${STORAGE_KEY}:trial:${trialCode}` : STORAGE_KEY
  /**
   * The draft this page load is still working through.
   *
   * Held in memory rather than re-read, because the second pass runs after the
   * account's data lands — by which time the save watcher has already rewritten
   * storage from the half-restored state, and re-reading would confirm the loss
   * instead of repairing it.
   */
  const pending = ref<CheckoutDraft | null>(null)

  /**
   * Each account-scoped field is applied AT MOST ONCE.
   *
   * The second pass re-runs whenever one of the account's lists settles, and
   * without this a list that arrives late — a manual VPC refresh, say — would
   * put the draft's network back over the one the customer had since chosen.
   * A restore may fill a gap; it may never overrule a decision made after it.
   */
  const consumed = new Set<keyof CheckoutDraft>()

  function read(): CheckoutDraft | null {
    if (!import.meta.client) return null
    try {
      const raw = window.sessionStorage.getItem(storageKey)
      if (!raw) return null
      const parsed = JSON.parse(raw) as CheckoutDraft
      if (parsed?.v !== VERSION) return null
      // Discard trial drafts saved in the shared key by the first release.
      if (parsed.trialCode && parsed.trialCode !== trialCode) return null
      if (!parsed.savedAt || Date.now() - parsed.savedAt > TTL_MS) return null
      return parsed
    } catch {
      // A private window, cleared storage, or a value another build wrote.
      return null
    }
  }

  function snapshot(
    configurator: CheckoutConfigurator,
    extras: CheckoutDraftExtras,
  ): CheckoutDraft {
    return {
      v: VERSION,
      savedAt: Date.now(),
      datacenterId: configurator.selectedDatacenterId.value,
      offeringCode: configurator.selectedOfferingCode.value,
      term: configurator.selectedTerm.value,
      osTab: configurator.osTab.value,
      templateCode: configurator.selectedTemplateCode.value,
      mainDiskExtraGb: configurator.mainDiskExtraGb.value,
      extraDisks: [...configurator.extraDisks.value],
      addonQuantities: { ...configurator.addonQuantities.value },
      connectivityMode: configurator.connectivityMode.value,
      vpcId: configurator.selectedVpcId.value,
      clientImageId: extras.clientImageId,
      couponCode: extras.couponCode || null,
      trialCode: extras.trialCode || null,
      initialCreditAmount: extras.initialCreditAmount ?? null,
      appInputs: extras.appInputs ?? null,
      targetSubscriptionId:
        extras.targetSubscriptionId && extras.targetSubscriptionId !== 'new'
          ? extras.targetSubscriptionId
          : null,
    }
  }

  function save(draft: CheckoutDraft): void {
    if (!import.meta.client) return
    // Nothing to remember until the customer has actually chosen something.
    if (
      !draft.datacenterId &&
      !draft.offeringCode &&
      !draft.templateCode &&
      !draft.clientImageId
    ) {
      return
    }
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(draft))
    } catch {
      // Storage disabled or full. A draft is a convenience; losing it must
      // never break the page the customer is on.
    }
  }

  function clear(): void {
    pending.value = null
    consumed.clear()
    if (!import.meta.client) return
    try {
      window.sessionStorage.removeItem(storageKey)
    } catch {
      /* see save() */
    }
  }

  /**
   * First pass: everything the public catalog can confirm on its own.
   *
   * Returns whether a choice THE CUSTOMER MADE came back — a datacenter the
   * page would have selected for them anyway is not worth announcing, and
   * announcing it would train people to ignore the notice.
   */
  function restore(
    configurator: CheckoutConfigurator,
    catalog: CatalogSnapshot | null | undefined,
    options: { datacenterIds?: number[] } = {},
  ): boolean {
    // A catalog that has not arrived cannot validate anything. Leave the draft
    // where it is and let the caller try again — clearing here is how a page
    // whose catalog request failed erased a configuration on the way past.
    if (!catalog) return false

    const draft = pending.value ?? read()
    if (!draft) return false
    pending.value = draft

    let announceable = false

    const offeringExists = catalog.offerings.some(
      (o) => o.code === draft.offeringCode && o.offering_kind === 'COMPUTE',
    )
    const templateExists = catalog.templates.some((tmpl) => tmpl.code === draft.templateCode)
    const termExists = catalog.terms.some((t) => t.kind === draft.term)

    // A location we cannot confirm is dropped: the picker would otherwise hold
    // an id that renders as nothing selected while the quote used it.
    if (
      draft.datacenterId != null &&
      (!options.datacenterIds || options.datacenterIds.includes(draft.datacenterId))
    ) {
      configurator.selectedDatacenterId.value = draft.datacenterId
    }
    if (termExists) {
      configurator.selectedTerm.value = draft.term
    }
    if (offeringExists && draft.offeringCode) {
      configurator.selectedOfferingCode.value = draft.offeringCode
      announceable = true
    }
    if (draft.osTab !== 'byo' && templateExists && draft.templateCode) {
      configurator.osTab.value = draft.osTab
      configurator.selectedTemplateCode.value = draft.templateCode
      announceable = true
    }

    if (Number.isFinite(draft.mainDiskExtraGb) && draft.mainDiskExtraGb > 0) {
      configurator.mainDiskExtraGb.value = draft.mainDiskExtraGb
      announceable = true
    }
    if (Array.isArray(draft.extraDisks) && draft.extraDisks.length > 0) {
      configurator.extraDisks.value = draft.extraDisks.filter(
        (d) => Number.isFinite(d?.size_gb) && d.size_gb > 0,
      )
      announceable = true
    }

    if (draft.connectivityMode === 'public' || draft.connectivityMode === 'private') {
      configurator.connectivityMode.value = draft.connectivityMode
      if (draft.connectivityMode === 'private') announceable = true
    }

    // Only add-ons the catalog still sells.
    const sellable = new Set((catalog.addons ?? []).map((a) => a.code))
    const quantities: Record<string, number> = {}
    for (const [code, quantity] of Object.entries(draft.addonQuantities ?? {})) {
      if (sellable.has(code) && Number.isFinite(quantity) && quantity > 0) {
        quantities[code] = quantity
      }
    }
    if (Object.keys(quantities).length > 0) {
      configurator.addonQuantities.value = quantities
      announceable = true
    }

    return announceable
  }

  /**
   * Second pass: the choices only the signed-in account can confirm.
   *
   * Safe to call repeatedly — each list is applied the first time it can be
   * checked, and a caller that only knows some of them passes only those. That
   * is what lets it run from every point the data can arrive: the initial load,
   * the sign-in that happens underneath the page, and a manual VPC refresh.
   *
   * Returns the page-level values it recovered, because a coupon and a target
   * subscription do not live on the configurator.
   */
  function restoreAccountScoped(
    configurator: CheckoutConfigurator,
    scope: CheckoutDraftAccountScope = {},
  ): { couponCode: string | null; targetSubscriptionId: string | null; applied: boolean } {
    const draft = pending.value
    if (!draft) return { couponCode: null, targetSubscriptionId: null, applied: false }

    let applied = false
    const take = (field: keyof CheckoutDraft) => {
      if (consumed.has(field)) return false
      consumed.add(field)
      applied = true
      return true
    }

    if (draft.vpcId != null && scope.vpcIds?.includes(draft.vpcId) && take('vpcId')) {
      configurator.selectedVpcId.value = draft.vpcId
    }

    // The BYO tab is only meaningful with an image on it, so both move together
    // — restoring the tab alone would land the customer on an empty picker that
    // reads as "your image is gone".
    if (
      draft.osTab === 'byo' &&
      draft.clientImageId &&
      scope.imageIds?.includes(draft.clientImageId) &&
      take('clientImageId')
    ) {
      configurator.osTab.value = 'byo'
      configurator.selectedClientImageId.value = draft.clientImageId
    }

    const targetSubscriptionId =
      draft.targetSubscriptionId &&
      scope.subscriptionIds?.includes(draft.targetSubscriptionId) &&
      take('targetSubscriptionId')
        ? draft.targetSubscriptionId
        : null

    // A coupon needs no account data to be re-typed into the field — the API
    // re-previews it and remains the authority on whether it applies.
    const couponCode = draft.couponCode && take('couponCode') ? draft.couponCode : null

    return { couponCode, targetSubscriptionId, applied }
  }

  return { read, snapshot, save, clear, restore, restoreAccountScoped, pending }
}
